import { Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useWatch } from 'react-hook-form';

import { Accordion } from '@/components/Accordion/Accordion';
import { Card } from '@/components/layout/Card/Card';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { DefaultLuminaryDisclaimer } from '@/modules/presentation/clientPresentation/DisclaimerSlide/DefaultLuminaryDisclaimer';
import { useDefaultDisclaimerSlideTitle } from '@/modules/presentation/clientPresentation/DisclaimerSlide/useDefaultDisclaimerSlideTitle';
import { PRESENTATION_SPACING_UNITS } from '@/modules/presentation/presentation.constants';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';

import { LegalDisclaimersFormShape } from './LegalDisclaimersForm';

export function StandaloneDisclaimerPreview() {
  const { control } = useFormContext<LegalDisclaimersFormShape>();
  const [
    presentationStandaloneDisclaimerTitle,
    presentationStandaloneDisclaimerBody,
    includeLuminaryDisclaimer,
  ] = useWatch({
    control,
    name: [
      'presentationStandaloneDisclaimerTitle',
      'presentationStandaloneDisclaimerBody',
      'includeLuminaryDisclaimer',
    ],
  });
  const defaultDisclaimerTitle = useDefaultDisclaimerSlideTitle();
  const title = presentationStandaloneDisclaimerTitle ?? defaultDisclaimerTitle;

  let body: ReactNode | undefined;
  if (presentationStandaloneDisclaimerBody) {
    body = presentationStandaloneDisclaimerBody;
  } else if (includeLuminaryDisclaimer) {
    body = <DefaultLuminaryDisclaimer />;
  }

  return (
    <Accordion title="Standalone disclaimer preview" variant="default">
      {body ? (
        <Card variant="outlined">
          <PresentationSlide.Slide
            id="preview-slide"
            leftHeaderContent={
              <PresentationSlide.MainHeading heading={title} />
            }
            footer={<PresentationSlide.Footer hideDisclaimer />}
          >
            <Stack p={PRESENTATION_SPACING_UNITS} height="100%">
              <Typography
                variant="body-s"
                // white-space: pre-wrap is necessary to preserve line breaks.
                // fontSize/lineHeight match the rendered disclaimer slide (12px),
                // which was set in #11005 but missed here in the preview.
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                }}
              >
                {body}
              </Typography>
            </Stack>
          </PresentationSlide.Slide>
        </Card>
      ) : (
        <Callout severity="warning">
          No standalone disclaimer configured. Please add standalone disclaimer
          content or enable the Luminary disclaimer slide.
        </Callout>
      )}
    </Accordion>
  );
}
